/* eslint-disable react/no-array-index-key */
/* eslint-disable no-continue */
import {css, Typography} from '@mui/material';
import {
  ArrayUtils,
  AuthorizationFetcher,
  BasicEnumDefinition,
  CHANGED_BY_OTHER_USER_STATUS,
  cleanLocalStorage,
  ConfigObjectType,
  DateUtils,
  DlMuiButton,
  DlMuiDeleteButton,
  DlMuiDynamicIcon,
  enumContainer,
  FetcherUtils,
  LightIconButton,
  LoadingSpinnerDialog,
  PopupWrapper,
  Riskiness,
  RISKINESS_COLORS,
  StringUtils,
  useChangedByOtherDialog,
  useCurrentLocale,
  useLocalStorage,
} from '@wallaby-ui/core';
import {
  copyToClipboard,
  Formatters,
  FreeSoloValue,
  GetDRPMedicationDataProps,
  getRiskFactorMedicationLabel,
  getSelectOptions,
  isFreeSolo,
  LightCheckbox2,
  LightDateFilter,
  LightErrorBox,
  LightHierarchyFilter,
  LightPreviewBox,
  LightRadio,
  LightRichTextAreaFilter,
  LightSelectFilter,
  LightSelectFilterProps,
  LightSelectFilterVirtualized,
  MerieSchema,
  NormalizedOption,
  Option,
  RISK_FACTOR_LABEL_COLORS,
  RiskFactorLabelEnum,
  RiskFactorTypeEnum,
  SelectParams,
  TooltipWrapper,
  useDrpCauseBackground,
  useDRPMedicationData,
  useFormatters,
  useInactiveAtcRenderProps,
  useInstanceLocation,
  useInstancePublishEnabled,
  useLightOrganizationOptions,
  useMedicationAtcs,
  useMedicationAtcsByMedicationId,
  usePermissions,
  useRichTextToText,
  useValidate,
  ValidateError,
  ValidateErrorStrings,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import {useSnackbar} from 'notistack';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Modal} from 'react-bootstrap';
import {useIntl} from 'react-intl';

import {DrpCombinationManagementDialog} from './combinations/DrpCombinationManagementDialog';
import {CombinationValueInput, useCombinations} from './combinations/useCombinations';
import {HospitalizationPhRecommendationChangeLogV2} from './HospitalizationPhRecommendationChangeLogV2';
import {
  DRPMedicationData,
  getIsPhRecUnpublished,
  MedicationAtc,
  MedicationType,
  PhRecDRP,
  PhRecommendation,
  pickGetDRPMedicationDataProps,
} from './HospitalizationPhRecommendationPanelV2';
import {useProceduresValidator} from './HospitalizationPhRecommendationProceduresValidatorV2';
import {useAppSelector} from 'src/js/store_hooks';

type PharmaRecommendationCreateUpdateDto = MerieSchema.components['schemas']['PharmaRecommendationCreateUpdateDto'];
type PhRiskFactor = PhRecommendation['riskFactors'][0];
type EncounterRiskFactor = MerieSchema.components['schemas']['RiskFactorDto'];
type DRPKeysWithErrors =
  | 'medicationName_local'
  | 'medicationAtcId'
  | 'type'
  | 'manifestation'
  | 'causes'
  | 'plannedInterventions'
  | 'plannedInterventionsUserCommentFormatted'
  | 'interventionAcceptance'
  | 'interventionNotAcceptedReason'
  | 'interventionNotAcceptedReasonUserCommentFormatted'
  | 'status'
  | 'statusAssessedOn'
  | 'notesFormatted';
type LocalDRP = Partial<Omit<PhRecDRP, 'medicationId' | 'medicationUserInput'>> & {
  medicationName_local?: string | FreeSoloValue;
  interventionAcceptance?: PhRecDRP['interventionAcceptance'];
  _toBeDeleted: boolean;
};

type PhRecState = {
  createdDate: string | null;
  lastModifiedDate: string | null;
  lastPublishDate: string | null;
  lastCheckedDate: string | null;
  organizationDepartmentId: number | null;
  riskiness: PhRecommendation['riskiness'] | null;
  riskFactors_local: (string | FreeSoloValue)[];
  summary: string;
  summaryFormatted?: string;
  conclusionIsWithoutRecommendation: boolean;
  conclusion: string;
  conclusionFormatted?: string;
  followUpDate: string | null;
  checked: boolean;
  procedures: PhRecommendation['procedures'];
  drugRelatedProblems: LocalDRP[];
};
type PhRecViewState = {
  preview: boolean;
  selectedDRPTab: number;
};
const DEFAULT_VIEW_STATE: PhRecViewState = {
  preview: false,
  selectedDRPTab: 0,
};
type IsUnchanged = Omit<Record<keyof PhRecState, boolean>, 'drugRelatedProblems' | 'conclusion' | 'summary'> & {
  drugRelatedProblems: Record<DRPKeysWithErrors, boolean>[];
};
function getWillDrpBeDeleted(phRecState: PhRecState, drp: LocalDRP): boolean {
  return drp._toBeDeleted;
}

type PhRecParamsKeys =
  | 'organizationDepartmentId'
  | 'riskiness'
  | 'riskFactors_local'
  | 'procedures'
  | 'drp_medicationName_local'
  | 'drp_medicationAtcId'
  | 'drp_type'
  | 'drp_causes'
  | 'drp_manifestation'
  | 'drp_plannedInterventions'
  | 'drp_interventionAcceptance'
  | 'drp_interventionNotAcceptedReason'
  | 'drp_status';
type PhRecParamsValue = Omit<LightSelectFilterProps, 'value'>;
type PhRecParams = Record<PhRecParamsKeys, PhRecParamsValue>;

// risk factors
function getRiskFactorKey(riskFactor: PhRiskFactor): string {
  return `${riskFactor.riskFactorType}-${riskFactor.riskFactorId ?? ''}-${riskFactor.riskFactorUserGivenName ?? ''}`;
}
function mapToLocalRiskFactors(riskFactors: PhRiskFactor[]): (string | FreeSoloValue)[] {
  return riskFactors.map((riskFactor) => {
    let v: string | FreeSoloValue;
    if (riskFactor.riskFactorType === 'OTHER') {
      v = {freeSolo: riskFactor.riskFactorUserGivenName ?? ''};
    } else {
      v = getRiskFactorKey(riskFactor);
    }
    return v;
  });
}
function useDefaultRiskFactors(encounterRiskFactors: EncounterRiskFactor[]) {
  const defaultRiskFactors = useMemo(() => {
    const acc: PhRiskFactor[] = [];
    // include all active RFs
    encounterRiskFactors.forEach((rf) => {
      // NOTE: BE version agnostic
      if (rf.isActive ?? true) {
        acc.push({
          riskFactorId: rf.id,
          riskFactorType: rf.type,
          riskFactorUserGivenName: rf.typeOtherUserGivenName,
        });
      }
    });
    return acc;
  }, [encounterRiskFactors]);

  return {
    defaultRiskFactors,
    defaultRiskiness: (defaultRiskFactors.length > 0 ? 'MEDIUM' : 'LOW') as PhRecommendation['riskiness'],
  };
}
type GetRiskFactorDataProps = {
  localSelectedRiskFactors: (string | FreeSoloValue)[];
  encounterRiskFactors: EncounterRiskFactor[];
  item?: PhRecommendation;
  enumMap: Record<string, BasicEnumDefinition[]>;
  sortByMessageKey: (v: BasicEnumDefinition[]) => BasicEnumDefinition[];
  formatters: Formatters;
};
function useRiskFactorData(props: GetRiskFactorDataProps) {
  const {localSelectedRiskFactors, encounterRiskFactors, item, enumMap, sortByMessageKey, formatters} = props;
  const {tEnum} = formatters;

  const encounterMedicationRiskFactors = useMemo(() => {
    const acc: EncounterRiskFactor[] = [];
    // include active and inactive RFs
    encounterRiskFactors.forEach((v) => {
      acc.push(v);
    });
    return acc;
  }, [encounterRiskFactors]);
  const encounterMedicationRiskFactorsMap = useMemo(() => {
    const acc = {} as Record<string, EncounterRiskFactor>;
    encounterMedicationRiskFactors.forEach((v) => {
      acc[v.id] = v;
    });
    return acc;
  }, [encounterMedicationRiskFactors]);

  const localRiskFactorToId = useMemo(() => {
    const acc = {} as Record<string, number>;
    (item?.riskFactors ?? []).forEach((v) => {
      acc[getRiskFactorKey(v)] = v.id as number;
    });
    return acc;
  }, [item]);
  const mapFromLocalRiskFactors = useCallback(
    (localRiskFactors: (string | FreeSoloValue)[]) => {
      const acc: PhRiskFactor[] = localRiskFactors.map((localRiskFactor) => {
        let v: PhRiskFactor;
        if (isFreeSolo(localRiskFactor)) {
          const riskFactorType = 'OTHER';
          const riskFactorUserGivenName = localRiskFactor.freeSolo;
          v = {
            id: localRiskFactorToId[`OTHER--${riskFactorUserGivenName}`] ?? undefined,
            riskFactorType,
            riskFactorUserGivenName,
          };
        } else {
          const rfKey = localRiskFactor;
          const match = rfKey.match(/([^-]*)-([^-]*)-(.*)/);
          const [_, riskFactorType, riskFactorId, riskFactorUserGivenName] = match as RegExpMatchArray;
          v = {
            id: localRiskFactorToId[rfKey] ?? undefined,
            riskFactorType: riskFactorType as PhRiskFactor['riskFactorType'],
            riskFactorId: riskFactorId ? +riskFactorId : undefined,
            riskFactorUserGivenName: riskFactorUserGivenName || undefined,
          };
        }
        return v;
      });
      return acc;
    },
    [localRiskFactorToId]
  );

  const riskFactorOptions = useMemo(() => {
    type RiskFactorOption = {
      id: string;
      label: string;
    };
    const acc: RiskFactorOption[] = [];
    const getRiskFactorOption = (v: PhRiskFactor): RiskFactorOption => {
      const medicationRf = v.riskFactorId ? encounterMedicationRiskFactorsMap[v.riskFactorId] : undefined;
      const medicationLabel = medicationRf ? getRiskFactorMedicationLabel(medicationRf) : undefined;
      return {
        ...v,
        id: getRiskFactorKey(v),
        label: tEnum('RiskFactorTypeEnum', v.riskFactorType) + (medicationLabel ? ` - ${medicationLabel}` : ''),
      };
    };
    // encounter risk factors
    const selectedRiskFactors = mapFromLocalRiskFactors(localSelectedRiskFactors);
    encounterMedicationRiskFactors.forEach((v) => {
      const selectedRiskFactor = selectedRiskFactors.find((w) => w.riskFactorType === v.type && v.type !== 'OTHER');
      const needOldOption = selectedRiskFactor != null && selectedRiskFactor.riskFactorId == null;
      if (needOldOption) {
        acc.push(getRiskFactorOption({riskFactorType: v.type}));
      } else {
        acc.push(
          getRiskFactorOption({
            riskFactorId: v.id,
            riskFactorType: v.type,
            riskFactorUserGivenName: v.typeOtherUserGivenName,
          })
        );
      }
    });
    // generic risk factors
    const genericRiskFactors = sortByMessageKey(enumMap.RiskFactorTypeEnum ?? []).filter((v) => v.name !== 'OTHER');
    genericRiskFactors.forEach((v) => {
      if (encounterMedicationRiskFactors.find((w) => w.type === v.name) != null) {
        return;
      }
      acc.push(
        getRiskFactorOption({
          riskFactorType: v.name as any,
        })
      );
    });
    return acc;
  }, [
    encounterMedicationRiskFactorsMap,
    tEnum,
    localSelectedRiskFactors,
    mapFromLocalRiskFactors,
    encounterMedicationRiskFactors,
    sortByMessageKey,
    enumMap.RiskFactorTypeEnum,
  ]);

  return {
    riskFactorOptions,
    mapFromLocalRiskFactors,
  };
}
// medication name
type PhRecMedicationName = Pick<PhRecDRP, 'medicationId' | 'medicationUserInput'>;
function getMedicationNameKey(v: PhRecMedicationName) {
  return String(v.medicationId ?? '');
}
export function mapToLocalMedicationName(medicationName: PhRecMedicationName): string | FreeSoloValue | undefined {
  if (medicationName.medicationUserInput) {
    return {freeSolo: medicationName.medicationUserInput ?? ''};
  }
  if (medicationName.medicationId) {
    return getMedicationNameKey(medicationName);
  }
  return undefined;
}
export function mapFromLocalMedicationName(localMedicationName: string | FreeSoloValue | undefined | null): PhRecMedicationName {
  if (localMedicationName == null) return {};
  if (isFreeSolo(localMedicationName)) {
    return {
      medicationUserInput: localMedicationName.freeSolo,
    };
  }
  return {
    medicationId: +localMedicationName,
  };
}
type UseMedicationOptionsProps = {
  medications: MedicationType[];
  getDRPMedicationData: (props: GetDRPMedicationDataProps) => DRPMedicationData;
  config: ConfigObjectType;
  selectedMedicationName: string | FreeSoloValue | undefined | null;
  selectedDrpMedicationName?: string | null;
};
export function useMedicationOptions(props: UseMedicationOptionsProps) {
  const {medications, getDRPMedicationData, config, selectedMedicationName, selectedDrpMedicationName} = props;
  const currentLocale = useCurrentLocale();

  // medicationName
  const medicationNameOptions = useMemo(() => {
    const acc = [] as Option[];
    const getMedicationNameOption = (v: PhRecMedicationName): Option => {
      return {
        id: getMedicationNameKey(v),
        label: getDRPMedicationData({
          medicationId: v.medicationId,
          medicationUserInput: v.medicationUserInput,
          medicationAtcId: -1, // NOTE: get label without atc
          medicationName: undefined,
        }).fullMedicationLabel,
      };
    };
    medications.forEach((v) => {
      acc.push(getMedicationNameOption({medicationId: v.id}));
    });

    const selectedMedicationId = mapFromLocalMedicationName(selectedMedicationName).medicationId;
    if (selectedMedicationId != null && !acc.find((o) => o.id === String(selectedMedicationId))) {
      acc.push({
        id: String(selectedMedicationId),
        label: getDRPMedicationData({
          medicationId: selectedMedicationId,
          medicationUserInput: undefined,
          medicationAtcId: -1,
          medicationName: selectedDrpMedicationName,
        }).fullMedicationLabel,
      });
    }

    return acc;
  }, [medications, getDRPMedicationData, selectedMedicationName, selectedDrpMedicationName]);

  const idToMedicationName = useMemo(() => {
    const acc = {} as Record<string, Option>;
    medicationNameOptions.forEach((v) => {
      acc[v.id as string] = v;
    });
    return acc;
  }, [medicationNameOptions]);

  // medicationAtcId
  const selectedDrpMedicationId = mapFromLocalMedicationName(selectedMedicationName).medicationId;

  const selectedMedicationIsActive = useMemo(() => {
    if (selectedDrpMedicationId == null) return true;
    return medications.find((m) => m.id === selectedDrpMedicationId) != null;
  }, [selectedDrpMedicationId, medications]);

  const {data: allMedicationAtcs, isLoading: allMedicationAtcsLoading} = useMedicationAtcs<MedicationAtc>(config);
  const {data: medicationAtcsByMedicationId, isLoading: medicationAtcsByMedicationIdLoading} =
    useMedicationAtcsByMedicationId<MedicationAtc>(config, selectedMedicationIsActive ? selectedDrpMedicationId : null); // null pokud neaktivní lék → žádný request

  const medicationAtcOptionsLoading = allMedicationAtcsLoading || medicationAtcsByMedicationIdLoading;

  const medicationAtcOptions = useMemo(() => {
    const base = selectedDrpMedicationId != null ? medicationAtcsByMedicationId : allMedicationAtcs;
    const collator = new Intl.Collator(currentLocale, {
      sensitivity: 'base',
      usage: 'sort',
      numeric: false,
    });
    return base.filter((atc) => atc.isActive).sort((a, b) => collator.compare(a.label, b.label));
  }, [selectedDrpMedicationId, medicationAtcsByMedicationId, allMedicationAtcs, currentLocale]);

  const idToMedicationAtc = useMemo(() => {
    const acc = {} as Record<string, MedicationAtc>;
    allMedicationAtcs.forEach((v) => {
      acc[v.id as number] = v;
    });
    return acc;
  }, [allMedicationAtcs]);

  return {medicationNameOptions, medicationAtcOptions, medicationAtcOptionsLoading, idToMedicationName, idToMedicationAtc};
}
export function isKnownMedicationName(medicationName_local: string | FreeSoloValue | undefined | null): boolean {
  return typeof medicationName_local === 'string';
}
// local DRP
function mapToLocalDRPs(drps: PhRecDRP[]): LocalDRP[] {
  return drps.map((v) => ({
    ...v,
    status: v.status === 'UNKNOWN' ? undefined : v.status,
    interventionAcceptance: v.interventionAcceptance === 'UNKNOWN' ? undefined : v.interventionAcceptance,
    medicationName_local: mapToLocalMedicationName(v),
    _toBeDeleted: false,
  }));
}
function mapFromLocalDRP(v: LocalDRP): PhRecDRP {
  const newPlannedInterventionsUserComment = v.plannedInterventionsUserComment || undefined;
  const newInterventionNotAcceptedReasonUserComment = v.interventionNotAcceptedReasonUserComment || undefined;
  const newNotes = v.notes || undefined;
  return {
    ...mapFromLocalMedicationName(v.medicationName_local),
    id: v.id,
    medicationAtcId: v.medicationAtcId as number,
    medicationAtcName: v.medicationAtcName as string,
    medicationAtcCode: v.medicationAtcCode as string,
    medicationAtcIsActive: v.medicationAtcIsActive as boolean,
    type: v.type as PhRecDRP['type'],
    manifestation: v.manifestation as PhRecDRP['manifestation'],
    causes: v.causes as PhRecDRP['causes'],
    plannedInterventions: v.plannedInterventions as PhRecDRP['plannedInterventions'],
    plannedInterventionsUserComment: newPlannedInterventionsUserComment,
    plannedInterventionsUserCommentFormatted: newPlannedInterventionsUserComment ? v.plannedInterventionsUserCommentFormatted : undefined,
    interventionAcceptance: v.interventionAcceptance == null ? 'UNKNOWN' : v.interventionAcceptance,
    interventionNotAcceptedReason: v.interventionNotAcceptedReason,
    interventionNotAcceptedReasonUserComment: newInterventionNotAcceptedReasonUserComment,
    interventionNotAcceptedReasonUserCommentFormatted: newInterventionNotAcceptedReasonUserComment
      ? v.interventionNotAcceptedReasonUserCommentFormatted
      : undefined,
    status: v.status,
    statusAssessedOn: v.statusAssessedOn,
    notes: newNotes,
    notesFormatted: newNotes ? v.notesFormatted : undefined,
  };
}
function stripConclusionSignature(conclusion: string): string {
  const lines = conclusion.split(/\r\n|\r|\n/); // NOTE: only very old macs use '\r'
  const lastLine = lines[lines.length - 1];
  if (lastLine.startsWith('- ')) {
    lines.pop();
  }
  return lines.join('\n');
}

type Props = {
  storageIdPrefix: string;
  onClose: (needRefetch: boolean) => void;
  item: PhRecommendation | undefined;
  encounterRiskFactors: EncounterRiskFactor[];
  medications: MedicationType[];
  getDRPMedicationData: (props: GetDRPMedicationDataProps) => DRPMedicationData;
  config: ConfigObjectType;
  hospitalizationId: string;
  defaultDepartmentId: number | null;
  riskFactorToRiskFactorLabel: Record<RiskFactorTypeEnum, RiskFactorLabelEnum>;
  phRecListEtag?: string;
};
export const HospitalizationPhRecommendationV2: React.FC<Props> = (props) => {
  const {
    storageIdPrefix,
    onClose: _onClose,
    item,
    encounterRiskFactors,
    medications,
    getDRPMedicationData,
    config,
    hospitalizationId,
    defaultDepartmentId,
    riskFactorToRiskFactorLabel,
    phRecListEtag,
  } = props;
  const onClose = useCallback(
    (needRefetch: boolean) => {
      cleanLocalStorage(storageIdPrefix);
      _onClose(needRefetch);
    },
    [storageIdPrefix, _onClose]
  );
  const formatters = useFormatters();
  const {t, tEnum, formatDate} = formatters;
  const snackbar = useSnackbar();

  // confirm popups
  const [confirmDiscard_anchorTag_top, setConfirmDiscard_anchorTag_top] = useState(null as HTMLElement | null);
  const [confirmDiscard_anchorTag_bottom, setConfirmDiscard_anchorTag_bottom] = useState(null as HTMLElement | null);
  const [confirmDelete_anchorTag, setConfirmDelete_anchorTag] = useState(null as HTMLElement | null);
  const [confirmSave_anchorTag, setConfirmSave_anchorTag] = useState(null as HTMLElement | null);
  const [confirmPreview_anchorTag, setConfirmPreview_anchorTag] = useState(null as HTMLElement | null);
  const [confirmSetConclusionIWR_anchorTag, setConfirmSetConclusionIWR_anchorTag] = useState(null as HTMLElement | null);

  // default risk factors
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  const {defaultRiskFactors, defaultRiskiness} = useDefaultRiskFactors(encounterRiskFactors);

  // prev state
  const prevState = useMemo(() => {
    const acc: PhRecState = {
      createdDate: item?.createdDate ?? null,
      lastModifiedDate: item?.lastModifiedDate ?? null,
      lastPublishDate: item?.lastPublishDate ?? null,
      lastCheckedDate: item?.lastCheckedDate ?? null,
      organizationDepartmentId: item?.organizationDepartmentId ?? null,
      riskiness: item?.riskiness ?? null,
      riskFactors_local: mapToLocalRiskFactors(item?.riskFactors ?? []),
      summary: item?.summary ?? '',
      summaryFormatted: item?.summaryFormatted ?? (item?.summary ? JSON.stringify(item?.summary) : undefined),
      conclusionIsWithoutRecommendation: item?.conclusionIsWithoutRecommendation ?? false,
      conclusion: stripConclusionSignature(item?.conclusion ?? ''),
      conclusionFormatted: item?.conclusionFormatted ?? (item?.conclusion ? JSON.stringify(item?.conclusion) : undefined),
      followUpDate: item?.followUpDate ?? null,
      checked: item?.checked ?? false,
      procedures: item?.procedures ?? [],
      drugRelatedProblems: mapToLocalDRPs(item?.drugRelatedProblems ?? []),
    };
    return acc;
  }, [item]);

  // default state for new PhRec
  const defaultPhRecState = useMemo(() => {
    const acc: PhRecState = {
      ...prevState,
      organizationDepartmentId: item?.organizationDepartmentId ?? defaultDepartmentId,
      riskiness: item?.riskiness ?? defaultRiskiness,
      riskFactors_local: mapToLocalRiskFactors(item?.riskFactors ?? defaultRiskFactors),
    };
    return acc;
  }, [prevState, item, defaultDepartmentId, defaultRiskFactors, defaultRiskiness]);

  // state
  const [phRecState, _1, setPhRecState] = useLocalStorage(`${storageIdPrefix}.phRecState`, defaultPhRecState);
  const [viewState, _2, setViewState] = useLocalStorage(`${storageIdPrefix}.viewState`, DEFAULT_VIEW_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isUnchanged = useMemo(() => {
    const acc: IsUnchanged = {
      createdDate: true,
      lastModifiedDate: true,
      lastPublishDate: true,
      lastCheckedDate: true,
      organizationDepartmentId: phRecState.organizationDepartmentId === prevState.organizationDepartmentId,
      riskiness: phRecState.riskiness === prevState.riskiness,
      riskFactors_local: isEqual(phRecState.riskFactors_local, prevState.riskFactors_local),
      summaryFormatted: phRecState.summaryFormatted === prevState.summaryFormatted,
      conclusionIsWithoutRecommendation: phRecState.conclusionIsWithoutRecommendation === prevState.conclusionIsWithoutRecommendation,
      conclusionFormatted: phRecState.conclusionFormatted === prevState.conclusionFormatted,
      followUpDate: phRecState.followUpDate === prevState.followUpDate,
      checked: phRecState.checked === prevState.checked,
      procedures: isEqual(phRecState.procedures, prevState.procedures),
      drugRelatedProblems: phRecState.drugRelatedProblems.map((v, i) => {
        const prevV = prevState.drugRelatedProblems[i] as LocalDRP | undefined;
        return {
          medicationName_local: isEqual(v.medicationName_local, prevV?.medicationName_local),
          medicationAtcId: v.medicationAtcId === prevV?.medicationAtcId,
          type: v.type === prevV?.type,
          manifestation: v.manifestation === prevV?.manifestation,
          causes: v.causes === prevV?.causes,
          plannedInterventions: v.plannedInterventions === prevV?.plannedInterventions,
          plannedInterventionsUserCommentFormatted:
            v.plannedInterventionsUserCommentFormatted === prevV?.plannedInterventionsUserCommentFormatted,
          interventionAcceptance: v.interventionAcceptance === prevV?.interventionAcceptance,
          interventionNotAcceptedReason: v.interventionNotAcceptedReason === prevV?.interventionNotAcceptedReason,
          interventionNotAcceptedReasonUserCommentFormatted:
            v.interventionNotAcceptedReasonUserCommentFormatted === prevV?.interventionNotAcceptedReasonUserCommentFormatted,
          status: v.status === prevV?.status,
          statusAssessedOn: v.statusAssessedOn === prevV?.statusAssessedOn,
          notesFormatted: v.notesFormatted === prevV?.notesFormatted,
          toBeDeleted: !getWillDrpBeDeleted(phRecState, v),
        };
      }),
    };
    return acc;
  }, [prevState, phRecState]);
  const haveUnsavedChanges = useMemo(() => {
    const getIsUnchanged = (current: Record<string, boolean | any>) => {
      return Object.values(current).every((v) => {
        return typeof v === 'boolean' ? v : v.every(getIsUnchanged);
      });
    };
    return !getIsUnchanged(isUnchanged) || item?.id == null;
  }, [isUnchanged, item?.id]);
  // etag
  const phRecEtag = useRef({
    etag: undefined as string | undefined,
    prevListEtag: undefined as string | undefined,
  });
  if (phRecListEtag !== phRecEtag.current.prevListEtag) {
    phRecEtag.current.etag = phRecListEtag;
    phRecEtag.current.prevListEtag = phRecListEtag;
  }
  const {changedByOtherDialog, showChangedByOtherDialog} = useChangedByOtherDialog({
    handleConfirm: () => {
      onClose(true);
    },
  });

  // organizations
  const {options: organizations /* , isLoading: organizationsLoading */} = useLightOrganizationOptions({appConfig: config});
  const organizationOptions = useMemo(() => {
    return organizations.map((v) => ({...v, disabled: v.levelType !== 'DEPARTMENT'}));
  }, [organizations]);
  // risk factors
  const {riskFactorOptions, mapFromLocalRiskFactors} = useRiskFactorData({
    localSelectedRiskFactors: phRecState.riskFactors_local,
    encounterRiskFactors,
    enumMap,
    item,
    sortByMessageKey,
    formatters,
  });

  const selectedDrp = phRecState.drugRelatedProblems[viewState.selectedDRPTab] as LocalDRP | undefined;
  const {medicationNameOptions, medicationAtcOptions, medicationAtcOptionsLoading, idToMedicationName, idToMedicationAtc} =
    useMedicationOptions({
      medications,
      getDRPMedicationData,
      config,
      selectedMedicationName: selectedDrp?.medicationName_local,
      selectedDrpMedicationName: selectedDrp?.medicationName,
    });

  // enums
  const procedureOptions = useMemo(() => sortByMessageKey(enumMap.Procedure ?? []), [enumMap.Procedure, sortByMessageKey]);
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  // phRecParams
  const riskinessChipCss = useCallback((selected: string | FreeSoloValue) => {
    const info = RISKINESS_COLORS[selected as string];
    return info
      ? css`
          background-color: ${info.background};
        `
      : undefined;
  }, []);
  const riskFactorChipCss = useCallback(
    (selected: string | FreeSoloValue) => {
      const {riskFactorType} = mapFromLocalRiskFactors([selected])[0];
      const riskFactorLabel = riskFactorToRiskFactorLabel[riskFactorType];
      const info = RISK_FACTOR_LABEL_COLORS[riskFactorLabel];
      return info
        ? css`
            background-color: ${info.color};
          `
        : undefined;
    },
    [mapFromLocalRiskFactors, riskFactorToRiskFactorLabel]
  );
  const phRecParams: PhRecParams = useMemo(
    () => ({
      organizationDepartmentId: {
        config: {name: 'organizationDepartmentId', ui: {label: 'hospitalization.detail.phRec.organizationDepartmentId'}},
        options: organizationOptions,
      },
      riskiness: {
        config: {name: 'riskiness', ui: {label: 'hospitalization.detail.phRec.riskiness'}},
        options: sortByMessageKey(enumMap.Riskiness ?? []),
        isClearable: false,
        showChipsInSingleSelect: true,
        chipCss: riskinessChipCss,
      },
      riskFactors_local: {
        config: {name: 'riskFactors', ui: {label: 'hospitalization.detail.phRec.riskFactors'}},
        optional: true,
        options: riskFactorOptions,
        multi: true,
        freeSolo: true,
        freeSoloFormat: `${tEnum('RiskFactorTypeEnum', 'OTHER')} - {}`,
        chipCss: riskFactorChipCss,
      },
      procedures: {
        config: {name: 'procedures', ui: {label: 'hospitalization.detail.phRec.procedures'}},
        optional: true,
        options: procedureOptions,
        multi: true,
      },
      drp_medicationName_local: {
        config: {name: 'medicationName', ui: {label: 'hospitalization.detail.phRec.drp.medicationName'}},
        optional: true,
        options: medicationNameOptions,
        freeSolo: true,
      },
      drp_medicationAtcId: {
        config: {name: 'medicationAtcId', ui: {label: 'hospitalization.detail.phRec.drp.medicationAtcId'}},
        options: medicationAtcOptions,
      },
      drp_type: {
        config: {name: 'type', ui: {label: 'hospitalization.detail.phRec.drp.type'}},
        optional: true,
        options: sortByMessageKey(enumMap.DrpType ?? []),
      },
      drp_manifestation: {
        config: {name: 'manifestation', ui: {label: ''}},
        options: sortByMessageKey(enumMap.DrpManifestation ?? []),
      },
      drp_causes: {
        config: {name: 'causes', ui: {label: 'hospitalization.detail.phRec.drp.causes'}},
        options: sortByMessageKey(enumMap.DrpCause ?? []).map((v) => ({
          ...v,
          disabled: v.hasChildren,
        })),
        multi: true,
      },
      drp_plannedInterventions: {
        config: {name: 'plannedInterventions', ui: {label: 'hospitalization.detail.phRec.drp.plannedInterventions'}},
        options: sortByMessageKey(enumMap.DrpPlannedIntervention ?? []),
        multi: true,
      },
      drp_interventionAcceptance: {
        config: {name: 'interventionAcceptance', ui: {label: 'hospitalization.detail.phRec.drp.interventionAcceptance'}},
        optional: true,
        options: sortByMessageKey((enumMap.DrpInterventionAcceptance ?? []).filter((v) => v.name !== 'UNKNOWN')),
      },
      drp_interventionNotAcceptedReason: {
        config: {name: 'interventionNotAcceptedReason', ui: {label: 'hospitalization.detail.phRec.drp.interventionNotAcceptedReason'}},
        optional: true,
        options: sortByMessageKey(enumMap.DrpInterventionNotAcceptedReason ?? []),
      },
      drp_status: {
        config: {name: 'status', ui: {label: 'hospitalization.detail.phRec.drp.status'}},
        optional: true,
        options: sortByMessageKey((enumMap.DrpStatus ?? []).filter((v) => v.name !== 'UNKNOWN')),
      },
    }),
    [
      tEnum,
      sortByMessageKey,
      organizationOptions,
      enumMap,
      riskinessChipCss,
      riskFactorChipCss,
      medicationNameOptions,
      medicationAtcOptions,
      procedureOptions,
      riskFactorOptions,
    ]
  );
  // onError
  const intl = useIntl();
  const onError = useCallback(
    (error: any) => {
      const {t, formatDate} = formatters;
      const errorMessage = error.message ?? '';
      if (errorMessage.startsWith(`${CHANGED_BY_OTHER_USER_STATUS} `)) {
        // common
        const getSelectInfos = (value: any, params: PhRecParamsValue) => getSelectOptions(intl, params as SelectParams).renderInfos(value);
        type InputInfo = {type: 'select'; params: PhRecParamsValue} | {type: 'checkbox'} | {type: 'text'} | {type: 'date'} | {type: 'none'};
        const formatLabel = (prefix: string, key: string) => {
          const translationKey = StringUtils.removeSuffix(key, '_local');
          return t(prefix + translationKey);
        };
        const formatValue = (value: any, inputInfo: InputInfo): string => {
          switch (inputInfo.type) {
            case 'select':
              return getSelectInfos(ArrayUtils.asArray(value), inputInfo.params)
                .map((v) => v.label)
                .join(', ');
            case 'checkbox':
              return value ? t('global.yes') : t('global.no');
            case 'text':
              return value ?? '';
            case 'date':
              return value ? formatDate(value) : '';
            case 'none':
              return '';
          }
          return '?';
        };
        // PhRec values
        type PhRecDiffKeys = keyof Omit<
          PhRecState,
          'createdDate' | 'lastModifiedDate' | 'lastPublishDate' | 'lastCheckedDate' | 'drugRelatedProblems' | 'conclusion' | 'summary'
        >;
        const phRecInputType: Record<PhRecDiffKeys, InputInfo> = {
          organizationDepartmentId: {type: 'select', params: phRecParams.organizationDepartmentId},
          riskiness: {type: 'select', params: phRecParams.riskiness},
          riskFactors_local: {type: 'select', params: phRecParams.riskFactors_local},
          summaryFormatted: {type: 'text'},
          conclusionIsWithoutRecommendation: {type: 'checkbox'},
          conclusionFormatted: {type: 'text'},
          followUpDate: {type: 'date'},
          checked: {type: 'checkbox'},
          procedures: {type: 'select', params: phRecParams.procedures},
        };
        const changedPhRecValues: [string, string][] = [];
        Object.entries(phRecState).forEach(([k, v]) => {
          if (k === 'drugRelatedProblems' || isUnchanged[k]) return;
          const inputInfo = phRecInputType[k];
          if (inputInfo == null) {
            console.error('No inputInfo for:', {k, v});
            return;
          }
          changedPhRecValues.push([formatLabel('hospitalization.detail.phRec.', k), formatValue(v, inputInfo)]);
        });
        // DRP values
        type DrpKeys = keyof Omit<LocalDRP, 'id' | '_toBeDeleted'>;
        const drpInputType: Omit<
          Record<DrpKeys, InputInfo>,
          'plannedInterventionsUserComment' | 'interventionNotAcceptedReasonUserComment' | 'notes'
        > = {
          medicationName_local: {type: 'select', params: phRecParams.drp_medicationName_local},
          medicationAtcId: {type: 'select', params: phRecParams.drp_medicationAtcId},
          type: {type: 'select', params: phRecParams.drp_type},
          manifestation: {type: 'select', params: phRecParams.drp_manifestation},
          causes: {type: 'select', params: phRecParams.drp_causes},
          plannedInterventions: {type: 'select', params: phRecParams.drp_plannedInterventions},
          plannedInterventionsUserCommentFormatted: {type: 'text'},
          interventionAcceptance: {type: 'select', params: phRecParams.drp_interventionAcceptance},
          interventionNotAcceptedReason: {type: 'select', params: phRecParams.drp_interventionNotAcceptedReason},
          interventionNotAcceptedReasonUserCommentFormatted: {type: 'text'},
          status: {type: 'select', params: phRecParams.drp_status},
          notesFormatted: {type: 'text'},
          statusAssessedOn: {type: 'none'},
        };
        for (let i = 0; i < isUnchanged.drugRelatedProblems.length; i++) {
          const drpIsUnchanged = isUnchanged.drugRelatedProblems[i];
          const drp = phRecState.drugRelatedProblems[i];
          Object.keys(drpIsUnchanged).forEach((key) => {
            if (drpIsUnchanged[key]) return;
            const label = formatLabel('hospitalization.detail.phRec.drp.', key);
            const fullLabel = `${label} (${i + 1})`;
            const value = drp[key];
            const inputInfo = drpInputType[key];
            if (inputInfo == null) {
              console.error('No inputInfo for:', {key, value});
              return;
            }
            if (inputInfo.type === 'none') return;
            changedPhRecValues.push([fullLabel, formatValue(value, inputInfo)] as [string, string]);
          });
        }
        showChangedByOtherDialog(Object.fromEntries(changedPhRecValues));
        setViewState({...viewState, preview: false});
      }
      FetcherUtils.errorHandler(error);
    },
    [showChangedByOtherDialog, formatters, intl, phRecState, phRecParams, isUnchanged, viewState, setViewState]
  );
  const {instanceLocation /* , instanceLocationLoading */} = useInstanceLocation(config);
  // actions
  const addPhRecUrl = config.service.url.hospitalizationPhRecommendationCreate;
  const editPhRecUrl = config.service.url.hospitalizationPhRecommendationEdit;
  const createOrUpdatePhRec = useCallback(
    (phRec: PharmaRecommendationCreateUpdateDto) => {
      const isEdit = phRec.id != null;
      let apiUrl = isEdit ? editPhRecUrl : addPhRecUrl;
      apiUrl = apiUrl.replace('{evaluatedEncounterId}', hospitalizationId);
      apiUrl = apiUrl.replace('{pharmaRecId}', phRec.id);
      const apiMethod = isEdit ? 'put' : 'post';
      return AuthorizationFetcher.interceptedFetch(
        apiUrl,
        {
          method: apiMethod,
          body: JSON.stringify(phRec),
        },
        {
          'Content-Type': 'application/json',
          // 'If-Match': phRecEtag.current.etag,
        }
      )
        .then((response) => {
          const newEtag = StringUtils.parseEtag(response.headers.get('Etag') ?? undefined);
          if (newEtag) {
            phRecEtag.current.etag = newEtag;
          }
          return FetcherUtils.processJsonResponse(response);
        })
        .catch((response) => onError(response))
        .catch(StringUtils.printErrorAndRethrow);
    },
    [addPhRecUrl, editPhRecUrl, hospitalizationId, onError]
  );
  const {errors, haveErrors, validate} = useValidate(() => {
    const err: ValidateErrorStrings = {};
    // PhRec
    if (!phRecState.organizationDepartmentId)
      err.organizationDepartmentId = t('hospitalization.detail.phRec.organizationDepartmentId.required');
    if (!phRecState.riskiness) err.riskiness = t('hospitalization.detail.phRec.riskiness.required');
    if (!phRecState.conclusion && !phRecState.conclusionIsWithoutRecommendation)
      err.conclusion = t('hospitalization.detail.phRec.conclusion.required');

    // DRPs
    for (let i = 0; i < phRecState.drugRelatedProblems.length; i++) {
      const drp = phRecState.drugRelatedProblems[i];
      if (getWillDrpBeDeleted(phRecState, drp)) {
        continue;
      }
      const prefix = `drugRelatedProblems.${i}`;
      if (drp.medicationAtcId == null) err[`${prefix}.medicationAtcId`] = t('hospitalization.detail.phRec.drp.medicationAtcId.required');
      // if (!drp.type) err[`${prefix}.type`] = t('hospitalization.detail.phRec.drp.type.required');
      if ((drp.causes ?? []).length === 0) err[`${prefix}.causes`] = t('hospitalization.detail.phRec.drp.causes.required');
      if ((drp.plannedInterventions ?? []).length === 0)
        err[`${prefix}.plannedInterventions`] = t('hospitalization.detail.phRec.drp.plannedInterventions.required');
    }
    return err;
  });
  const conclusionIsWithoutRecommendationConclusionText = t('hospitalization.detail.phRec.conclusion.noDRPText');
  const phRecDataToSave = useMemo(() => {
    const nowIso = new Date().toISOString();
    const newLastCheckedDate = phRecState.checked ? nowIso : phRecState.lastCheckedDate ?? undefined;
    const newSummary = phRecState.summary || undefined;
    const newConclusion = phRecState.conclusion;
    return {
      lastModifiedDate: phRecState.lastModifiedDate ?? new Date().toISOString(),
      lastPublishDate: phRecState.lastPublishDate ?? undefined,
      lastCheckedDate: newLastCheckedDate,
      organizationDepartmentId: phRecState.organizationDepartmentId as number,
      riskiness: phRecState.riskiness as PhRecommendation['riskiness'],
      riskFactors: mapFromLocalRiskFactors(phRecState.riskFactors_local),
      summary: newSummary,
      summaryFormatted: newSummary ? phRecState.summaryFormatted : undefined,
      conclusionIsWithoutRecommendation: phRecState.conclusionIsWithoutRecommendation,
      conclusion: newConclusion,
      conclusionFormatted: phRecState.conclusionFormatted,
      followUpDate: phRecState.followUpDate ?? undefined,
      checked: phRecState.checked,
      procedures: phRecState.procedures,
      drugRelatedProblems: phRecState.drugRelatedProblems.filter((v) => !getWillDrpBeDeleted(phRecState, v)).map(mapFromLocalDRP),
    };
  }, [phRecState, mapFromLocalRiskFactors]);
  const {validateProcedures, modal: proceduresValidatorModal} = useProceduresValidator({
    config,
    evaluatedEncounterId: hospitalizationId,
    pharmaRecommendationId: item?.id,
    riskiness: phRecState.riskiness as Riskiness,
    procedures: phRecState.procedures,
    riskFactorCount: phRecState.riskFactors_local.length,
    instanceLocation: instanceLocation?.toUpperCase(),
    onCancelCallback: () => setIsSubmitting(false),
    onConfirmCallback: () => setIsSubmitting(false),
  });
  const saveAsDraft = useCallback(
    (eventTarget: any) => {
      if (!validate()) {
        return;
      }
      setIsSubmitting(true);
      validateProcedures(async () => {
        try {
          if (haveUnsavedChanges) {
            await createOrUpdatePhRec({
              ...phRecDataToSave,
              id: item?.id ?? undefined,
            });
            onClose(true);
          } else {
            onClose(false);
          }
        } finally {
          setIsSubmitting(false);
        }
      }, eventTarget as HTMLElement);
    },
    [validate, haveUnsavedChanges, createOrUpdatePhRec, item, phRecDataToSave, onClose, validateProcedures]
  );
  // publish
  const {userRoles, keycloakTokenParsed} = usePermissions(config);
  const currentUser_name = keycloakTokenParsed.current?.name;
  const conclusionToPublish = useMemo(() => {
    return `${phRecDataToSave.conclusion}`;
  }, [phRecDataToSave.conclusion]);
  const phRecStateToPublish = useMemo(
    () => ({
      ...phRecState,
      conclusion: conclusionToPublish,
    }),
    [phRecState, conclusionToPublish]
  );
  const phRecDataToPublish = useMemo(
    () => ({
      ...phRecDataToSave,
      conclusion: conclusionToPublish,
    }),
    [phRecDataToSave, conclusionToPublish]
  );
  const {instancePublishEnabled, instancePublishEnabledLoading} = useInstancePublishEnabled(config);
  const canPublish = useMemo(() => {
    if (instancePublishEnabledLoading || !instancePublishEnabled) return false;
    return userRoles.RECOMMENDATION_PUBLISH_ROLE ?? false;
  }, [instancePublishEnabledLoading, instancePublishEnabled, userRoles.RECOMMENDATION_PUBLISH_ROLE]);
  const publishPhRecUrl = config.service.url.hospitalizationPhRecommendationPublish;
  const currentLocale = useCurrentLocale();
  const publishPhRec = useCallback(
    (phRecId: number) => {
      let apiUrl = publishPhRecUrl.replace();
      apiUrl = apiUrl.replace('{evaluatedEncounterId}', hospitalizationId);
      apiUrl = apiUrl.replace('{pharmaRecId}', phRecId);
      return AuthorizationFetcher.interceptedFetch(
        apiUrl,
        {
          method: 'POST',
          body: JSON.stringify({
            localization: currentLocale.toUpperCase(),
          }),
        },
        {
          'Content-Type': 'application/json',
          // 'If-Match': phRecEtag.current.etag,
        }
      )
        .then((response) => FetcherUtils.processEmptyContentResponse(response))
        .catch((response) => onError(response))
        .catch(StringUtils.printErrorAndRethrow);
    },
    [publishPhRecUrl, hospitalizationId, currentLocale, onError]
  );
  const onCancelPublish = useCallback(() => {
    setViewState({...viewState, preview: false});
  }, [viewState, setViewState]);
  const onPublish = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // NOTE: we always have to update the PhRec, because we need to at least update the conclusion field with the signature
      const createOrUpdateResponse: PhRecommendation = await createOrUpdatePhRec({
        ...phRecDataToPublish,
        id: item?.id ?? undefined,
        // lastPublishDate: new Date().toISOString(),
        // isDirty: false,
      });
      const phRecId = (createOrUpdateResponse?.id ?? item?.id) as number;
      await publishPhRec(phRecId);
      onClose(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [createOrUpdatePhRec, phRecDataToPublish, item, publishPhRec, onClose]);
  const richTextToText = useRichTextToText();
  const exportTextUrl = config.service.url.hospitalizationPhRecommendationExportText;
  const exportToClipboard = useCallback(async () => {
    try {
      const response = await AuthorizationFetcher.interceptedFetch(
        exportTextUrl,
        {
          method: 'POST',
          body: JSON.stringify({
            ...phRecDataToSave,
            summary: richTextToText(phRecDataToSave.summaryFormatted) ?? undefined,
            conclusion: richTextToText(phRecDataToSave.conclusionFormatted) ?? undefined,
            drugRelatedProblems: phRecDataToSave.drugRelatedProblems.map((drp) => ({
              ...drp,
              plannedInterventionsUserComment: richTextToText(drp.plannedInterventionsUserCommentFormatted) ?? undefined,
              interventionNotAcceptedReasonUserComment: richTextToText(drp.interventionNotAcceptedReasonUserCommentFormatted) ?? undefined,
              notes: richTextToText(drp.notesFormatted) ?? undefined,
            })),
            localization: currentLocale.toUpperCase(),
          }),
        },
        {
          'Content-Type': 'application/json',
        }
      );
      const responseText = await response.text();
      await copyToClipboard(responseText, snackbar, formatters);
    } catch (error) {
      // noop
    }
  }, [exportTextUrl, phRecDataToSave, richTextToText, currentLocale, snackbar, formatters]);

  // confirm discard
  const confirmDiscard_onConfirm = useCallback(() => {
    onClose(false);
    setConfirmDiscard_anchorTag_top(null);
    setConfirmDiscard_anchorTag_bottom(null);
  }, [onClose]);
  const discardOrOpenConfirmPopup_top = useCallback(
    (event: Event) => {
      if (haveUnsavedChanges) {
        setConfirmDiscard_anchorTag_top(event.target as HTMLElement);
      } else {
        confirmDiscard_onConfirm();
      }
    },
    [haveUnsavedChanges, confirmDiscard_onConfirm]
  );
  const discardOrOpenConfirmPopup_bottom = useCallback(
    (event: Event) => {
      if (haveUnsavedChanges) {
        setConfirmDiscard_anchorTag_bottom(event.target as HTMLElement);
      } else {
        confirmDiscard_onConfirm();
      }
    },
    [haveUnsavedChanges, confirmDiscard_onConfirm]
  );

  // confirm delete
  const openConfirmDeletePopup = useCallback((event: Event) => {
    setConfirmDelete_anchorTag(event.target as HTMLElement);
  }, []);
  const confirmDelete_onConfirm = useCallback(() => {
    setConfirmDelete_anchorTag(null);
    try {
      setIsSubmitting(true);
      let deleteUrl = config.service.url.hospitalizationPhRecommendationDelete;
      deleteUrl = deleteUrl.replace('{evaluatedEncounterId}', hospitalizationId);
      deleteUrl = deleteUrl.replace('{pharmaRecId}', item?.id);
      AuthorizationFetcher.interceptedFetch(
        deleteUrl,
        {method: 'DELETE'},
        {
          'Content-Type': 'application/json',
          // 'If-Match': phRecEtag.current.etag,
        }
      )
        .then((response) => {
          const newEtag = StringUtils.parseEtag(response.headers.get('Etag') ?? undefined);
          if (newEtag) {
            phRecEtag.current.etag = newEtag;
          }
          return FetcherUtils.processJsonResponse(response);
        })
        .catch((response) => onError(response))
        .catch(StringUtils.printErrorAndRethrow)
        .finally(() => onClose(true));
    } finally {
      setIsSubmitting(false);
    }
  }, [config, hospitalizationId, item, onError, onClose]);

  const mediumWarningText = t('hospitalization.detail.phRec.riskiness.medium.validation');
  const highWarningText = t('hospitalization.detail.phRec.riskiness.high.validation');
  const previewContinueText = t('hospitalization.detail.phRec.preview.continue');
  const saveConceptText = t('hospitalization.detail.phRec.concept.save');

  // confirm save
  const serverIsCzech = instanceLocation?.toUpperCase() === 'CZ';
  const confirmSave_messages = useMemo(() => {
    if (!serverIsCzech) return undefined;
    if (phRecState.riskFactors_local.length > 0) return undefined;
    const messageMap = {
      MEDIUM: [mediumWarningText, saveConceptText],
      HIGH: [highWarningText, saveConceptText],
    } as Record<Riskiness, string[]>;
    return messageMap[phRecState.riskiness as Riskiness];
  }, [serverIsCzech, phRecState.riskFactors_local.length, phRecState.riskiness, mediumWarningText, saveConceptText, highWarningText]);
  const confirmSave_onConfirm = useCallback(
    (event?: any) => {
      const anchor = confirmSave_anchorTag;
      setConfirmSave_anchorTag(null);
      return saveAsDraft(anchor || event?.target);
    },
    [saveAsDraft, confirmSave_anchorTag]
  );
  const saveOrOpenConfirmPopup = useCallback(
    (event: Event) => {
      if (confirmSave_messages) {
        setConfirmSave_anchorTag(event.target as HTMLElement);
      } else {
        confirmSave_onConfirm(event);
      }
    },
    [confirmSave_messages, confirmSave_onConfirm]
  );

  // confirm preview
  const confirmPreview_messages = useMemo(() => {
    if (!serverIsCzech) return undefined;
    if (phRecState.riskFactors_local.length > 0) return undefined;
    const messageMap = {
      MEDIUM: [mediumWarningText, previewContinueText],
      HIGH: [highWarningText, previewContinueText],
    } as Record<Riskiness, string[]>;
    return messageMap[phRecState.riskiness as Riskiness];
  }, [serverIsCzech, phRecState.riskFactors_local.length, phRecState.riskiness, mediumWarningText, previewContinueText, highWarningText]);
  const confirmPreview_onConfirm = useCallback(
    (event?: React.MouseEvent) => {
      const anchor = confirmPreview_anchorTag;
      setConfirmPreview_anchorTag(null);
      validateProcedures(() => {
        setConfirmPreview_anchorTag(null);
        setViewState({...viewState, preview: true});
      }, anchor || (event?.target as HTMLElement));
    },
    [confirmPreview_anchorTag, validateProcedures, setViewState, viewState]
  );
  const openPreviewOrOpenConfirmPopup = useCallback(
    (event: Event) => {
      if (validate()) {
        if (confirmPreview_messages) {
          setConfirmPreview_anchorTag(event.target as HTMLElement);
        } else {
          confirmPreview_onConfirm(event as any);
        }
      }
    },
    [validate, confirmPreview_messages, confirmPreview_onConfirm]
  );
  useEffect(() => {
    if (phRecState.conclusionIsWithoutRecommendation && phRecState.conclusion !== conclusionIsWithoutRecommendationConclusionText) {
      setPhRecState({
        ...phRecState,
        conclusion: conclusionIsWithoutRecommendationConclusionText,
        conclusionFormatted: JSON.stringify(conclusionIsWithoutRecommendationConclusionText),
      });
    }
  }, [conclusionIsWithoutRecommendationConclusionText, phRecState, setPhRecState]);
  const confirmSetConclusionIWR_onConfirm = useCallback(() => {
    setConfirmSetConclusionIWR_anchorTag(null);
    setPhRecState({
      ...phRecState,
      conclusionIsWithoutRecommendation: true,
    });
  }, [phRecState, setPhRecState, setConfirmSetConclusionIWR_anchorTag]);
  const confirmSetConclusionIWR_needConfirmation =
    phRecState.conclusion && phRecState.conclusion !== conclusionIsWithoutRecommendationConclusionText;
  const confirmSetConclusionIWR_onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, newValue: boolean) => {
      if (newValue && confirmSetConclusionIWR_needConfirmation) {
        setConfirmSetConclusionIWR_anchorTag(event.target as HTMLElement);
      } else {
        setPhRecState({
          ...phRecState,
          conclusionIsWithoutRecommendation: newValue,
        });
      }
    },
    [confirmSetConclusionIWR_needConfirmation, phRecState, setPhRecState]
  );
  const deleteButtonTooltip =
    item?.lastPublishDate != null
      ? item?.canBeDeletedByCurrentUser
        ? t('hospitalization.detail.phRec.cannotDeletePublishedByOwner')
        : t('hospitalization.detail.phRec.cannotDeletePublishedByOther')
      : undefined;

  return (
    <>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <PopupWrapper
          openButton={
            <DlMuiButton style={{height: 26}} type='chip' title={'common.confirmDialog.back'} onClick={discardOrOpenConfirmPopup_top} />
          }
          anchorTag={confirmDiscard_anchorTag_top}
          direction='down'
          yOffset={4}
          showDiscardSaveButtons
          cancelTitle='global.no'
          confirmTitle='global.yes'
          onClose={() => setConfirmDiscard_anchorTag_top(null)}
          onConfirm={confirmDiscard_onConfirm}
          data-name={'confirm.discard.dialog'}
        >
          <Typography variant='sectionHeader'>{t('hospitalization.detail.phRec.confirm.discard')}</Typography>
        </PopupWrapper>
        <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, paddingRight: 4}}>
          <TooltipWrapper wrapperStyle={{padding: 0}} tooltip={t('hospitalization.detail.phRec.changelog.open')}>
            <DlMuiDynamicIcon
              iconName='checkClipIcon'
              useFixedViewBox={12}
              style={{
                borderRadius: 4,
                border: 'none',
                background: 'var(--dlThemeGray-50)',
                padding: 5,
                width: 26,
                height: 26,
                color: 'rgb(8 126 139 / 80%)',
              }}
              svgIconStyle={{
                width: '100%',
                height: '100%',
              }}
              onClick={() => setShowChangelogModal(true)}
            />
          </TooltipWrapper>
          <TooltipWrapper wrapperStyle={{padding: 0}} tooltip={t('hospitalization.detail.phRec.exportToClipboard')}>
            <DlMuiDynamicIcon
              iconName='copyIcon'
              useFixedViewBox={12}
              style={{
                borderRadius: 4,
                border: 'none',
                background: 'var(--dlThemeGray-50)',
                padding: 5,
                width: 26,
                height: 26,
                color: 'rgb(8 126 139 / 80%)',
              }}
              svgIconStyle={{
                width: '100%',
                height: '100%',
              }}
              onClick={exportToClipboard}
            />
          </TooltipWrapper>
        </div>
      </div>
      <div style={{display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', flexWrap: 'wrap', columnGap: 16, rowGap: 8}}>
        <div style={{display: 'inline-flex', gap: 8, color: 'var(--dlThemeGray-800)'}}>
          <Typography variant='valueBold'>{t('hospitalization.detail.phRec.createdDate')}:</Typography>
          <Typography variant='value'>{phRecState.createdDate ? formatDate(phRecState.createdDate) : '-'}</Typography>
        </div>
        <div style={{display: 'inline-flex', gap: 8, color: 'var(--dlThemeGray-800)'}}>
          <Typography variant='valueBold'>{t('hospitalization.detail.phRec.lastModifiedDate')}:</Typography>
          <Typography variant='value'>{phRecState.lastModifiedDate ? formatDate(phRecState.lastModifiedDate) : '-'}</Typography>
        </div>
        <div style={{display: 'inline-flex', gap: 8, color: 'var(--dlThemeGray-800)'}}>
          <Typography variant='valueBold'>{t('hospitalization.detail.phRec.lastPublishDate')}:</Typography>
          <Typography variant='value'>{phRecState.lastPublishDate ? formatDate(phRecState.lastPublishDate) : '-'}</Typography>
          {getIsPhRecUnpublished(item) && (
            <TooltipWrapper wrapperStyle={{padding: 0}} tooltip={t('hospitalization.detail.phRec.unpublished')}>
              <DlMuiDynamicIcon
                iconName='merieSpikedExclamationIcon'
                useFixedViewBox={14}
                style={{padding: 0, marginLeft: 8}}
                svgIconStyle={{
                  width: 18,
                  height: 18,
                }}
              />
            </TooltipWrapper>
          )}
        </div>
      </div>
      <div style={{display: 'inline-flex', gap: 8, color: 'var(--dlThemeGray-800)'}}>
        <Typography variant='valueBold'>{t('hospitalization.detail.phRec.lastCheckedDate')}:</Typography>
        <Typography variant='value'>{phRecState.lastCheckedDate ? formatDate(phRecState.lastCheckedDate) : '-'}</Typography>
      </div>
      <div className='ph-rec-box'>
        <LightHierarchyFilter
          maxWidth='100%'
          {...phRecParams.organizationDepartmentId}
          value={phRecState.organizationDepartmentId}
          onChange={(newValue) =>
            setPhRecState({
              ...phRecState,
              organizationDepartmentId: newValue,
            })
          }
          ignoreOption={(option) => option.original.isSoloRootNode}
          isDefaultValue={isUnchanged.organizationDepartmentId}
          error={errors.organizationDepartmentId}
        />
      </div>
      <div className='ph-rec-box'>
        <LightSelectFilter
          maxWidth='100%'
          {...phRecParams.riskiness}
          value={phRecState.riskiness}
          onChange={(newValue) =>
            setPhRecState({
              ...phRecState,
              riskiness: newValue,
            })
          }
          isDefaultValue={isUnchanged.riskiness}
          error={errors.riskiness}
        />
      </div>
      <div className='ph-rec-box'>
        <LightSelectFilter
          maxWidth='100%'
          {...phRecParams.riskFactors_local}
          value={phRecState.riskFactors_local}
          onChange={(newValue: (string | FreeSoloValue)[]) =>
            setPhRecState({
              ...phRecState,
              riskFactors_local: newValue ?? [],
            })
          }
          isDefaultValue={isEqual(phRecState.riskFactors_local, prevState.riskFactors_local)}
        />
      </div>
      <div className='ph-rec-box'>
        <LightRichTextAreaFilter
          config={{name: 'summary', ui: {label: 'hospitalization.detail.phRec.summary'}}}
          optional
          value={phRecState.summaryFormatted ? JSON.parse(phRecState.summaryFormatted) : undefined}
          onChange={(json, html, text) => {
            setPhRecState({
              ...phRecState,
              summary: text ?? undefined,
              summaryFormatted: text ? JSON.stringify(json) : undefined,
            });
          }}
          isDefaultValue={isUnchanged.summaryFormatted}
          canMaximize
          slotProps={{
            Root: {
              style: {padding: '8px 0', maxWidth: '100%'},
            },
          }}
        />
      </div>
      <div className='ph-rec-box'>
        <PopupWrapper
          openButton={
            <LightCheckbox2
              style={{alignSelf: 'start', padding: '8px 0 0 0'}}
              label={t('hospitalization.detail.phRec.conclusionIsWithoutRecommendation')}
              value={phRecState.conclusionIsWithoutRecommendation}
              onChange={confirmSetConclusionIWR_onChange}
            />
          }
          anchorTag={confirmSetConclusionIWR_anchorTag}
          direction='down'
          yOffset={4}
          showKeepDeleteButtons
          cancelTitle='global.no'
          confirmTitle='global.yes'
          onClose={() => setConfirmSetConclusionIWR_anchorTag(null)}
          onConfirm={confirmSetConclusionIWR_onConfirm}
          contentStyle={{maxWidth: 340}}
        >
          {t('hospitalization.detail.phRec.confirm.conclusionIsWithoutRecommendation')
            .split('\n')
            .map((line, i) => (
              <Typography key={i} variant='sectionHeader' textAlign='center'>
                {line}
              </Typography>
            ))}
        </PopupWrapper>
        <LightRichTextAreaFilter
          config={{name: 'conclusion', ui: {label: 'hospitalization.detail.phRec.conclusion'}}}
          value={phRecState.conclusion ? JSON.parse(phRecState.conclusionFormatted ?? '') : undefined}
          onChange={(json, html, text) => {
            setPhRecState({
              ...phRecState,
              conclusion: text ?? undefined,
              conclusionFormatted: text ? JSON.stringify(json) : undefined,
            });
          }}
          isDefaultValue={isUnchanged.conclusionFormatted}
          canMaximize
          disabled={phRecState.conclusionIsWithoutRecommendation}
          error={errors.conclusion?.bool}
          helperText={errors.conclusion?.string}
          slotProps={{
            Root: {
              style: {padding: '8px 0', maxWidth: '100%'},
            },
          }}
        />
      </div>
      <div className='ph-rec-box'>
        <LightDateFilter
          style={{maxWidth: '100%'}}
          config={{
            name: `followUpDate`,
            ui: {label: 'patient.list.grid.pharmaRecommendationFollowUpDate'},
          }}
          optional
          value={phRecState.followUpDate}
          onChange={(newValue) => {
            setPhRecState({
              ...phRecState,
              followUpDate: newValue ?? '',
            });
          }}
          isDefaultValue={isUnchanged.followUpDate}
          placement='bottom-start'
          enableFuture
          disablePast
          disabled={phRecState.checked}
          showClearButton
        />
        <LightCheckbox2
          style={{
            alignSelf: 'start',
            padding: '0 0 8px 0',
            marginTop: -10,
            // ...(phRecState.checked ? {padding: '6px 0 8px 0', marginTop: 0} : {}),
          }}
          label={t('hospitalization.detail.phRec.checked')}
          value={phRecState.checked}
          onChange={(_event, newValue) => {
            let newFollowUpDate = phRecState.followUpDate;
            if (newValue && !newFollowUpDate) newFollowUpDate = DateUtils.toIsoDateString(new Date());
            setPhRecState({
              ...phRecState,
              checked: newValue,
              followUpDate: newFollowUpDate,
            });
          }}
        />
      </div>
      {procedureOptions.length > 0 && (
        <div className='ph-rec-box'>
          <LightSelectFilter
            maxWidth='100%'
            {...phRecParams.procedures}
            value={phRecState.procedures}
            onChange={(newValue) =>
              setPhRecState({
                ...phRecState,
                procedures: newValue ?? [],
              })
            }
            isDefaultValue={isUnchanged.procedures}
          />
        </div>
      )}
      <HospitalizationPhRecommendationV2DRPInputs
        config={config}
        medicationAtcOptionsLoading={medicationAtcOptionsLoading}
        medicationAtcOptions={medicationAtcOptions}
        phRecState={phRecState}
        setPhRecState={setPhRecState}
        viewState={viewState}
        setViewState={setViewState}
        phRecParams={phRecParams}
        formatters={formatters}
        idToMedicationName={idToMedicationName}
        idToMedicationAtc={idToMedicationAtc}
        isUnchanged={isUnchanged}
        errors={errors}
      />
      {haveErrors && <LightErrorBox message={t('hospitalization.detail.phRec.invalidChanges')} />}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            rowGap: 4,
          }}
        >
          <PopupWrapper
            openButton={
              <DlMuiButton
                style={{height: 30}}
                type='chip'
                title={'common.confirmDialog.discard'}
                onClick={discardOrOpenConfirmPopup_bottom}
              />
            }
            anchorTag={confirmDiscard_anchorTag_bottom}
            direction='up'
            yOffset={4}
            showDiscardSaveButtons
            cancelTitle='global.no'
            confirmTitle='global.yes'
            onClose={() => setConfirmDiscard_anchorTag_bottom(null)}
            onConfirm={confirmDiscard_onConfirm}
          >
            <Typography variant='sectionHeader'>{t('hospitalization.detail.phRec.confirm.discard')}</Typography>
          </PopupWrapper>
          {item != null && (
            <PopupWrapper
              openButton={
                <DlMuiButton
                  style={{height: 30}}
                  type='chip-red'
                  title={'common.confirmDialog.delete'}
                  onClick={openConfirmDeletePopup}
                  disabled={!item.canBeDeletedByCurrentUser || item.lastPublishDate != null}
                  disabledTooltip={deleteButtonTooltip}
                />
              }
              anchorTag={confirmDelete_anchorTag}
              direction='up'
              yOffset={4}
              showKeepDeleteButtons
              cancelTitle='common.confirmDialog.cancel'
              confirmTitle='common.confirmDialog.delete'
              onClose={() => setConfirmDelete_anchorTag(null)}
              onConfirm={confirmDelete_onConfirm}
            >
              <Typography variant='sectionHeader'>{t('hospitalization.detail.phRec.confirm.delete')}</Typography>
            </PopupWrapper>
          )}
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            rowGap: 4,
            justifyContent: 'flex-end',
          }}
        >
          <PopupWrapper
            openButton={
              <DlMuiButton
                style={{height: 30}}
                type='chip'
                title={t('hospitalization.detail.phRec.saveAsDraft')}
                onClick={saveOrOpenConfirmPopup}
                disabled={!userRoles.ENCOUNTER_DETAIL_EDIT_ROLE || isSubmitting}
              />
            }
            anchorTag={confirmSave_anchorTag}
            yOffset={4}
            direction='up'
            showDiscardSaveButtons
            cancelTitle='common.confirmDialog.cancel'
            confirmTitle='common.confirmDialog.confirm'
            onClose={() => setConfirmSave_anchorTag(null)}
            onConfirm={confirmSave_onConfirm}
          >
            {confirmSave_messages?.map((line, i) => (
              <Typography key={i} variant='sectionHeader' textAlign='center'>
                {line}
              </Typography>
            ))}
          </PopupWrapper>
          <PopupWrapper
            openButton={
              <DlMuiButton
                style={{height: 30}}
                type='chip-filled'
                title={t('hospitalization.detail.phRec.preview')}
                onClick={openPreviewOrOpenConfirmPopup}
                disabled={!canPublish}
                disabledTooltip={
                  instancePublishEnabled
                    ? t('hospitalization.detail.phRec.preview.disabled.role')
                    : t('hospitalization.detail.phRec.preview.disabled.instance')
                }
              />
            }
            anchorTag={confirmPreview_anchorTag}
            yOffset={4}
            direction='up'
            showDiscardSaveButtons
            cancelTitle='global.no'
            confirmTitle='global.yes'
            onClose={() => setConfirmPreview_anchorTag(null)}
            onConfirm={confirmPreview_onConfirm}
          >
            {confirmPreview_messages?.map((line, i) => (
              <Typography key={i} variant='sectionHeader' textAlign='center'>
                {line}
              </Typography>
            ))}
          </PopupWrapper>
        </div>
      </div>
      {proceduresValidatorModal}
      {viewState.preview && (
        <HospitalizationPhRecommendationV2PreviewDialog
          phRecState={phRecStateToPublish}
          signature={`- ${currentUser_name}, ${formatDate(new Date().toISOString())}`}
          phRecParams={phRecParams}
          onCancel={onCancelPublish}
          onPublish={onPublish}
          formatters={formatters}
        />
      )}
      {changedByOtherDialog}
      <LoadingSpinnerDialog loading={isSubmitting} />
      {showChangelogModal && (
        <HospitalizationPhRecommendationChangeLogV2
          config={config}
          changelog={item?.changeLog}
          medications={medications}
          getDRPMedicationData={getDRPMedicationData}
          encounterRiskFactors={encounterRiskFactors}
          organizations={organizations}
          onClose={() => setShowChangelogModal(false)}
        />
      )}
    </>
  );
};

type DRPInputsProps = {
  config: ConfigObjectType;
  medicationAtcOptionsLoading: boolean;
  medicationAtcOptions: MedicationAtc[];
  phRecState: PhRecState;
  setPhRecState: (newState: PhRecState) => void;
  viewState: PhRecViewState;
  setViewState: (newState: PhRecViewState) => void;
  phRecParams: PhRecParams;
  formatters: Formatters;
  idToMedicationName: Record<string, Option>;
  idToMedicationAtc: Record<string, MedicationAtc>;
  isUnchanged: IsUnchanged;
  errors: Record<string, ValidateError>;
};
const HospitalizationPhRecommendationV2DRPInputs: React.FC<DRPInputsProps> = (props) => {
  const {
    medicationAtcOptionsLoading,
    medicationAtcOptions,
    phRecState,
    setPhRecState,
    viewState,
    setViewState,
    phRecParams,
    formatters,
    idToMedicationName,
    idToMedicationAtc,
    isUnchanged,
    errors,
    config,
  } = props;
  const {t} = formatters;

  const visibleDrps = useMemo(() => {
    return phRecState.drugRelatedProblems
      .map((drp, originalIndex) => ({drp, originalIndex}))
      .filter(({drp}) => !getWillDrpBeDeleted(phRecState, drp));
  }, [phRecState]);

  const selectedVisibleIndex = useMemo(() => {
    const selectedDrp = phRecState.drugRelatedProblems[viewState.selectedDRPTab];
    if (!selectedDrp || getWillDrpBeDeleted(phRecState, selectedDrp)) {
      return 0;
    }
    return visibleDrps.findIndex(({originalIndex}) => originalIndex === viewState.selectedDRPTab);
  }, [phRecState, viewState.selectedDRPTab, visibleDrps]);

  const actualSelectedIndex = useMemo(() => {
    if (selectedVisibleIndex >= 0 && selectedVisibleIndex < visibleDrps.length) {
      return visibleDrps[selectedVisibleIndex].originalIndex;
    }
    return visibleDrps.length > 0 ? visibleDrps[0].originalIndex : 0;
  }, [selectedVisibleIndex, visibleDrps]);

  useEffect(() => {
    const selectedDrp = phRecState.drugRelatedProblems[viewState.selectedDRPTab];
    if (selectedDrp && getWillDrpBeDeleted(phRecState, selectedDrp) && visibleDrps.length > 0) {
      setViewState({
        ...viewState,
        selectedDRPTab: visibleDrps[0].originalIndex,
      });
    }
  }, [phRecState, viewState, visibleDrps, setViewState]);

  // addNewDrp
  const addNewDrp = useCallback(() => {
    const newDrugRelatedProblems = [...phRecState.drugRelatedProblems];
    const DEFAULT_DRP: LocalDRP = {
      manifestation: 'POTENTIAL_PROBLEM',
      _toBeDeleted: false,
    };
    newDrugRelatedProblems.push(DEFAULT_DRP);
    setPhRecState({
      ...phRecState,
      drugRelatedProblems: newDrugRelatedProblems,
    });
    setViewState({
      ...viewState,
      selectedDRPTab: newDrugRelatedProblems.length - 1,
    });
  }, [phRecState, setPhRecState, viewState, setViewState]);

  const selectedDrpState = useMemo(() => {
    return phRecState.drugRelatedProblems[actualSelectedIndex];
  }, [phRecState, actualSelectedIndex]);

  const changeSelectedDrpState = useCallback(
    (diff: Partial<LocalDRP>) => {
      const newDrugRelatedProblems = [...phRecState.drugRelatedProblems];
      const newDrp = {...selectedDrpState, ...diff};
      newDrugRelatedProblems.splice(actualSelectedIndex, 1, newDrp);
      setPhRecState({
        ...phRecState,
        drugRelatedProblems: newDrugRelatedProblems, // TODO:
      });
    },
    [phRecState, selectedDrpState, setPhRecState, actualSelectedIndex]
  );

  // automatically set medicationAtcId
  const automaticallySetMedicationAtcId = useRef(false);
  if (automaticallySetMedicationAtcId.current && !medicationAtcOptionsLoading && medicationAtcOptions.length > 0) {
    automaticallySetMedicationAtcId.current = false;
    if (medicationAtcOptions.length === 1) {
      setTimeout(() => {
        changeSelectedDrpState({
          ...selectedDrpState,
          medicationAtcId: medicationAtcOptions[0].id,
        });
      }, 0);
    }
  }

  // unchanged, error
  const selectedIsUnchanged = useMemo(
    () => isUnchanged.drugRelatedProblems[actualSelectedIndex] ?? {},
    [isUnchanged.drugRelatedProblems, actualSelectedIndex]
  );
  const selectedErrorPrefix = `drugRelatedProblems.${actualSelectedIndex}`;
  const [confirmDeleteDrpIndex, setConfirmDeleteDrpIndex] = useState<number | null>(null);
  const [confirmDeleteDrpAnchor, setConfirmDeleteDrpAnchor] = useState<HTMLElement | null>(null);
  const deleteDrp = useCallback(() => {
    if (confirmDeleteDrpIndex == null) return;
    const newDrugRelatedProblems = [...phRecState.drugRelatedProblems];
    const newDrp: LocalDRP = {
      ...newDrugRelatedProblems[confirmDeleteDrpIndex],
    };
    newDrp._toBeDeleted = !newDrp._toBeDeleted;
    newDrugRelatedProblems.splice(confirmDeleteDrpIndex, 1, newDrp);
    setPhRecState({
      ...phRecState,
      drugRelatedProblems: newDrugRelatedProblems,
    });
    setConfirmDeleteDrpIndex(null);
    setConfirmDeleteDrpAnchor(null);
  }, [confirmDeleteDrpIndex, phRecState, setPhRecState]);
  const parseFormattedFieldValue = (json: string | undefined, text: string | undefined) => {
    const jsonStringValue = json || JSON.stringify(text ?? '');
    return JSON.parse(jsonStringValue);
  };
  const getDrpCauseBackground = useDrpCauseBackground();
  const getDrpCauseBackgroundColor = useCallback(
    (cause: any) =>
      css({
        backgroundColor: getDrpCauseBackground(cause),
      }),
    [getDrpCauseBackground]
  );
  const {getDRPMedicationData, medications, isLoading: dprMedicationDataLoading} = useDRPMedicationData(config);
  const createDrpTitle = useCallback(
    (drp, index) => {
      const titleProps = {
        ...pickGetDRPMedicationDataProps(drp),
        ...mapFromLocalMedicationName(drp.medicationName_local !== null ? drp.medicationName_local : ''),
      };
      const {fullMedicationLabel} = getDRPMedicationData(titleProps);
      if (!fullMedicationLabel) {
        return t('hospitalization.detail.phRec.drp.label').replace('{n}', String(index + 1));
      }
      return fullMedicationLabel;
    },
    [getDRPMedicationData, t]
  );

  const {
    instances,
    instanceOptions,
    instancesLoading,
    createInstance,
    updateInstance,
    deleteInstance,
    refetchInstances,
    shortcuts,
    shortcutsLoading,
    updateShortcuts,
    showCreateDialog,
    openCreateDialog,
    closeCreateDialog,
  } = useCombinations({
    config,
    domain: 'DRP',
  });

  const [selectedCombinationId, setSelectedCombinationId] = useState<string | null>(null);

  const handleCombinationSelect = useCallback(
    (newValue: string | null) => {
      setSelectedCombinationId(newValue);

      if (!newValue) {
        changeSelectedDrpState({type: undefined, causes: undefined, plannedInterventions: undefined});
        return;
      }

      if (!selectedDrpState) return;

      const instance = instances.find((inst) => inst.id === Number(newValue));
      if (!instance?.values || instance.values.length === 0) return;

      const typeValue = instance.values.find((v) => v.key.dimension === 'DRP_TYPE')?.text as MerieSchema.components['schemas']['DrpType'];
      const causesValues = instance.values
        .filter((v) => v.key.dimension === 'DRP_CAUSE')
        .map((v) => v.text as MerieSchema.components['schemas']['DrpCause']);
      const interventionsValues = instance.values
        .filter((v) => v.key.dimension === 'DRP_INTERVENTION')
        .map((v) => v.text as MerieSchema.components['schemas']['DrpPlannedIntervention']);

      changeSelectedDrpState({
        type: typeValue,
        causes: causesValues,
        plannedInterventions: interventionsValues,
      });
    },
    [instances, selectedDrpState, changeSelectedDrpState]
  );

  const handleCreateCombination = useCallback(
    async (label: string, values: CombinationValueInput[]) => {
      await createInstance(label, values, (newInstanceId) => {
        setSelectedCombinationId(String(newInstanceId));
      });
    },
    [createInstance]
  );

  const handleUpdateCombination = useCallback(
    async (instanceId: number, label: string, values: CombinationValueInput[]) => {
      await updateInstance(instanceId, label, values);
    },
    [updateInstance]
  );

  const handleDeleteCombination = useCallback(
    async (instanceId: number) => {
      await deleteInstance(instanceId);
      if (selectedCombinationId === String(instanceId)) {
        setSelectedCombinationId(null);
      }
    },
    [deleteInstance, selectedCombinationId]
  );

  useEffect(() => {
    if (!selectedDrpState) return;

    const matchingInstance = instances?.find((instance) => {
      const type = instance.values?.find((v) => v.key.dimension === 'DRP_TYPE')?.text as MerieSchema.components['schemas']['DrpType'];
      const causes = instance.values
        ?.filter((v) => v.key.dimension === 'DRP_CAUSE')
        ?.map((cause) => cause.text as MerieSchema.components['schemas']['DrpCause']);
      const plannedInterventions = instance.values
        ?.filter((v) => v.key.dimension === 'DRP_INTERVENTION')
        ?.map((cause) => cause.text as MerieSchema.components['schemas']['DrpPlannedIntervention']);

      const typeMatches = type === selectedDrpState?.type;

      const causesMatch = isEqual(sortBy(causes || []), sortBy(selectedDrpState?.causes || []));

      const interventionsMatch = isEqual(sortBy(plannedInterventions || []), sortBy(selectedDrpState?.plannedInterventions || []));

      return typeMatches && causesMatch && interventionsMatch;
    });

    if (matchingInstance && String(matchingInstance.id) !== selectedCombinationId) {
      setSelectedCombinationId(String(matchingInstance.id));
    } else if (!matchingInstance && selectedCombinationId !== null) {
      setSelectedCombinationId(null);
    }
  }, [
    selectedDrpState?.type,
    selectedDrpState?.causes,
    selectedDrpState?.plannedInterventions,
    instances,
    selectedDrpState,
    selectedCombinationId,
  ]);

  const {renderOption: atcRenderOption, renderTag: atcRenderTag, getNodeIcon: atcGetNodeIcon} = useInactiveAtcRenderProps(t);

  const selectedAtcId = selectedDrpState?.medicationAtcId;
  const selectedAtcIsInOptions = selectedAtcId != null && medicationAtcOptions.some((atc) => atc.id === selectedAtcId);
  const selectedAtcFallbackOption = useMemo(() => {
    if (selectedAtcId == null || selectedAtcIsInOptions) return null;
    return idToMedicationAtc[selectedAtcId] ?? null;
  }, [selectedAtcId, selectedAtcIsInOptions, idToMedicationAtc]);
  const medicationAtcOptionsActiveWithFallback = useMemo(() => {
    if (selectedAtcFallbackOption == null || medicationAtcOptions.some((atc) => atc.id === selectedAtcFallbackOption.id)) {
      return medicationAtcOptions;
    }
    return [...medicationAtcOptions, selectedAtcFallbackOption];
  }, [medicationAtcOptions, selectedAtcFallbackOption]);

  return (
    <>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
        <div style={{flex: 1, display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 8, columnGap: 4}}>
          {visibleDrps.map(({drp, originalIndex}, visibleIndex) => (
            <PopupWrapper
              key={originalIndex}
              openButton={
                <DRPChip
                  key={originalIndex}
                  label={createDrpTitle(drp, visibleIndex)}
                  hasErrors={Object.keys(errors).findIndex((v) => v.startsWith(`drugRelatedProblems.${originalIndex}`)) !== -1}
                  isSelected={originalIndex === actualSelectedIndex}
                  willBeRemoved={false}
                  onSelect={() => {
                    setViewState({
                      ...viewState,
                      selectedDRPTab: originalIndex,
                    });
                  }}
                  onRemove={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setConfirmDeleteDrpIndex(originalIndex);
                    setConfirmDeleteDrpAnchor(event.currentTarget);
                  }}
                  dataName={'drp.chip'}
                />
              }
              anchorTag={confirmDeleteDrpAnchor}
              direction='down'
              yOffset={4}
              showKeepDeleteButtons
              cancelTitle='global.no'
              confirmTitle='global.yes'
              onClose={() => {
                setConfirmDeleteDrpAnchor(null);
                setConfirmDeleteDrpIndex(null);
              }}
              onConfirm={deleteDrp}
            >
              <Typography variant='sectionHeader'>{t('hospitalization.detail.phRec.drp.confirm.delete')}</Typography>
            </PopupWrapper>
          ))}
        </div>
        <DlMuiButton
          type='chip-filled'
          title={t('hospitalization.detail.phRec.drp.addNew')}
          tooltip={t('hospitalization.detail.phRec.drp.addNew.long')}
          onClick={addNewDrp}
        />
      </div>
      {visibleDrps.length > 0 && selectedDrpState && (
        <>
          <div className='ph-rec-box' style={{rowGap: 4}}>
            <LightSelectFilterVirtualized
              key={actualSelectedIndex}
              maxWidth='100%'
              style={{paddingBottom: 0}}
              {...phRecParams.drp_medicationName_local}
              value={selectedDrpState.medicationName_local}
              onChange={(newValue) => {
                automaticallySetMedicationAtcId.current = true;
                changeSelectedDrpState({
                  medicationName_local: newValue,
                  medicationAtcId: isKnownMedicationName(newValue) ? undefined : selectedDrpState.medicationAtcId,
                });
              }}
              isDefaultValue={selectedIsUnchanged.medicationName_local}
            />
            <LightHierarchyFilter
              maxWidth='100%'
              {...phRecParams.drp_medicationAtcId}
              options={medicationAtcOptionsActiveWithFallback}
              value={selectedDrpState.medicationAtcId != null ? String(selectedDrpState.medicationAtcId) : null}
              onChange={(newValue) => {
                changeSelectedDrpState({
                  medicationAtcId: newValue != null ? +newValue : undefined,
                });
              }}
              isDefaultValue={selectedIsUnchanged.medicationAtcId}
              error={errors[`${selectedErrorPrefix}.medicationAtcId`]}
              ignoreOption={(option: NormalizedOption<MedicationAtc>) => option.original.isSoloRootNode}
              getNodeIcon={(params) => atcGetNodeIcon(params.node)}
              renderOption={atcRenderOption}
              renderTag={atcRenderTag}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 5,
              alignItems: 'center',
              fontSize: 10,
              borderRadius: '8px',
              padding: '0 8px',
            }}
          >
            {shortcuts &&
              shortcuts.map((shortcut, i) => {
                const isShortcutSelected = selectedCombinationId === String(shortcut.combinationInstanceId);
                return (
                  <LightIconButton
                    key={i}
                    iconName={shortcut.iconName!}
                    style={{width: '100%', height: '100%'}}
                    wrapperStyle={{padding: '4px'}}
                    selected={isShortcutSelected}
                    title={shortcut.combinationInstanceName}
                    onClick={() => {
                      handleCombinationSelect(String(shortcut.combinationInstanceId));
                    }}
                  />
                );
              })}
            <LightSelectFilter
              style={{
                flex: 1,
                minWidth: 0,
                height: '100%',
                padding: '8px 0',
                fontSize: 'inherit',
                lineHeight: 1,
                boxSizing: 'border-box',
                maxHeight: '100%',
              }}
              autocompleteCss={css`
                font-size: inherit;
              `}
              config={{
                name: 'drpCombination',
                ui: {label: 'hospitalization.detail.phRec.combination.combobox.title'},
              }}
              options={instanceOptions}
              value={selectedCombinationId}
              onChange={handleCombinationSelect}
              renderOption={(props, option, rowInfo, ownerState) => {
                const {label} = option as any;
                const {iconName} = (option as any).original as any;
                const icon = iconName ? (
                  <DlMuiDynamicIcon
                    iconName={iconName}
                    noButtonStyles
                    style={{pointerEvents: 'none'}}
                    svgIconStyle={{width: '20px', height: '20px', color: 'var(--dlLightIconButtonFill)'}}
                    sx={{p: 0, m: 0}}
                  />
                ) : (
                  <div style={{display: 'inline-block', width: '20px', height: '20px'}} />
                );
                return (
                  <div {...props} key={`${rowInfo?.index}`} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                    {icon} <span style={{display: 'inline-block', marginLeft: '7px'}}>{t(label || '')}</span>
                  </div>
                );
              }}
            />
            <LightIconButton
              iconName='penToSquare'
              style={{width: '100%', height: '100%'}}
              title={'hospitalization.detail.phRec.combination.setup.button'}
              onClick={openCreateDialog}
              selected
            />
          </div>
          <div className='ph-rec-box'>
            <LightSelectFilter
              maxWidth='100%'
              style={{paddingBottom: 0}}
              {...phRecParams.drp_type}
              value={selectedDrpState.type}
              onChange={(newValue) =>
                changeSelectedDrpState({
                  type: newValue,
                })
              }
              isDefaultValue={selectedIsUnchanged.type}
              error={errors[`${selectedErrorPrefix}.type`]}
            />
            <LightRadio
              style={{paddingBottom: 8}}
              config={phRecParams.drp_manifestation.config}
              options={phRecParams.drp_manifestation.options.map((v) => ({
                id: v.name as string,
                label: t(v.messageKey ?? ''),
              }))}
              value={selectedDrpState.manifestation}
              onChange={(newValue) => {
                changeSelectedDrpState({
                  manifestation: newValue,
                });
              }}
            />
          </div>
          <div className='ph-rec-box'>
            <LightHierarchyFilter
              maxWidth='100%'
              {...phRecParams.drp_causes}
              defaultIsExpanded
              value={selectedDrpState.causes}
              onChange={(newValue) => {
                changeSelectedDrpState({
                  causes: newValue ?? undefined,
                });
              }}
              chipCss={getDrpCauseBackgroundColor}
              isDefaultValue={selectedIsUnchanged.causes}
              error={errors[`${selectedErrorPrefix}.causes`]}
            />
          </div>
          <div className='ph-rec-box'>
            <LightSelectFilter
              maxWidth='100%'
              style={{paddingBottom: 0}}
              {...phRecParams.drp_plannedInterventions}
              value={selectedDrpState.plannedInterventions}
              onChange={(newValue) =>
                changeSelectedDrpState({
                  plannedInterventions: newValue ?? undefined,
                })
              }
              isDefaultValue={selectedIsUnchanged.plannedInterventions}
              error={errors[`${selectedErrorPrefix}.plannedInterventions`]}
            />
            <LightRichTextAreaFilter
              config={{
                name: 'plannedInterventionsUserComment',
                ui: {label: 'hospitalization.detail.phRec.drp.plannedInterventionsUserComment'},
              }}
              optional
              value={parseFormattedFieldValue(
                selectedDrpState.plannedInterventionsUserCommentFormatted,
                selectedDrpState.plannedInterventionsUserComment
              )}
              onChange={(json, html, text) => {
                changeSelectedDrpState({
                  plannedInterventionsUserComment: text ?? undefined,
                  plannedInterventionsUserCommentFormatted: text ? JSON.stringify(json) : undefined,
                });
              }}
              isDefaultValue={selectedIsUnchanged.plannedInterventionsUserCommentFormatted}
              canMaximize
              slotProps={{
                Root: {
                  style: {padding: '8px 0', maxWidth: '100%'},
                },
              }}
            />
          </div>
          <div className='ph-rec-box' style={{rowGap: 0}}>
            <LightSelectFilter
              maxWidth='100%'
              {...phRecParams.drp_interventionAcceptance}
              value={selectedDrpState.interventionAcceptance}
              onChange={(newValue) =>
                changeSelectedDrpState({
                  interventionAcceptance: newValue ?? undefined,
                  interventionNotAcceptedReason: newValue !== 'NOT_ACCEPTED' ? undefined : selectedDrpState.interventionNotAcceptedReason,
                  interventionNotAcceptedReasonUserComment:
                    newValue !== 'NOT_ACCEPTED' ? undefined : selectedDrpState.interventionNotAcceptedReasonUserComment,
                  interventionNotAcceptedReasonUserCommentFormatted:
                    newValue !== 'NOT_ACCEPTED' ? undefined : selectedDrpState.interventionNotAcceptedReasonUserCommentFormatted,
                })
              }
              isDefaultValue={selectedIsUnchanged.interventionAcceptance}
              error={errors[`${selectedErrorPrefix}.interventionAcceptance`]}
            />
            {selectedDrpState.interventionAcceptance === 'NOT_ACCEPTED' && (
              <>
                <LightSelectFilter
                  maxWidth='100%'
                  style={{paddingTop: 2}}
                  {...phRecParams.drp_interventionNotAcceptedReason}
                  value={selectedDrpState.interventionNotAcceptedReason}
                  onChange={(newValue) =>
                    changeSelectedDrpState({
                      interventionNotAcceptedReason: newValue ?? undefined,
                    })
                  }
                  isDefaultValue={selectedIsUnchanged.interventionNotAcceptedReason}
                  error={errors[`${selectedErrorPrefix}.interventionNotAcceptedReason`]}
                />
                <LightRichTextAreaFilter
                  config={{
                    name: 'interventionNotAcceptedReasonUserComment',
                    ui: {label: 'hospitalization.detail.phRec.drp.interventionNotAcceptedReasonUserComment'},
                  }}
                  optional
                  value={parseFormattedFieldValue(
                    selectedDrpState.interventionNotAcceptedReasonUserCommentFormatted,
                    selectedDrpState.interventionNotAcceptedReasonUserComment
                  )}
                  onChange={(json, html, text) => {
                    changeSelectedDrpState({
                      interventionNotAcceptedReasonUserComment: text ?? undefined,
                      interventionNotAcceptedReasonUserCommentFormatted: text ? JSON.stringify(json) : undefined,
                    });
                  }}
                  isDefaultValue={selectedIsUnchanged.interventionNotAcceptedReasonUserCommentFormatted}
                  canMaximize
                  slotProps={{
                    Root: {
                      style: {padding: '8px 0', maxWidth: '100%'},
                    },
                  }}
                />
              </>
            )}
          </div>
          <div className='ph-rec-box' style={{rowGap: 4}}>
            <LightSelectFilter
              maxWidth='100%'
              style={{paddingBottom: selectedDrpState.status ? 0 : undefined}}
              {...phRecParams.drp_status}
              value={selectedDrpState.status}
              onChange={(newValue) =>
                changeSelectedDrpState({
                  status: newValue ?? undefined,
                  statusAssessedOn: newValue ? DateUtils.toIsoDateString(new Date()) : undefined,
                })
              }
              isDefaultValue={selectedIsUnchanged.status}
              error={errors[`${selectedErrorPrefix}.status`]}
            />
            {selectedDrpState.status && (
              <LightDateFilter
                style={{maxWidth: '100%', marginTop: 0}}
                config={{
                  name: `statusAssessedOn`,
                  ui: {label: 'hospitalization.detail.phRec.drp.statusAssessedOn'},
                }}
                optional
                value={selectedDrpState.statusAssessedOn}
                onChange={(newValue) => {
                  changeSelectedDrpState({
                    statusAssessedOn: newValue ?? '',
                  });
                }}
                isDefaultValue={selectedIsUnchanged.statusAssessedOn}
                placement='bottom-start'
              />
            )}
          </div>
          <div className='ph-rec-box'>
            <LightRichTextAreaFilter
              config={{name: 'notes', ui: {label: 'hospitalization.detail.phRec.drp.notes'}}}
              optional
              value={parseFormattedFieldValue(selectedDrpState.notesFormatted, selectedDrpState.notes)}
              onChange={(json, html, text) => {
                changeSelectedDrpState({
                  notes: text ?? undefined,
                  notesFormatted: text ? JSON.stringify(json) : undefined,
                });
              }}
              isDefaultValue={selectedIsUnchanged.notesFormatted}
              canMaximize
              slotProps={{
                Root: {
                  style: {padding: '8px 0', maxWidth: '100%'},
                },
              }}
            />
          </div>
          <DrpCombinationManagementDialog
            show={showCreateDialog}
            onClose={closeCreateDialog}
            onCreate={handleCreateCombination}
            onUpdate={handleUpdateCombination}
            onDelete={handleDeleteCombination}
            drpParams={{
              drp_type: phRecParams.drp_type,
              drp_causes: phRecParams.drp_causes,
              drp_plannedInterventions: phRecParams.drp_plannedInterventions,
            }}
            instances={instances}
            isLoadingInstances={instancesLoading}
            refetchInstances={refetchInstances}
            shortcuts={shortcuts}
            isLoadingShortcuts={shortcutsLoading}
            onUpdateShortcuts={updateShortcuts}
          />
        </>
      )}
    </>
  );
};

type DRPChipProps = {
  label: string;
  hasErrors: boolean;
  isSelected: boolean;
  willBeRemoved: boolean;
  onSelect: () => void;
  onRemove: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  dataName?: string;
};
const DRPChip: React.FC<DRPChipProps> = (props) => {
  const {label, hasErrors, isSelected, willBeRemoved, onSelect, onRemove, dataName} = props;
  const {background, color, iconColorHex, iconDarkColorHex} = useMemo(() => {
    let background = '';
    let color = 'white';
    let iconColorHex = willBeRemoved ? '#FA4D56' : '#ffffff';
    let iconDarkColorHex = '#DA1E28';
    if (isSelected) {
      background = 'var(--dlTheme-500)';
    } else if (hasErrors) {
      background = '#DA1E28';
      iconColorHex = willBeRemoved ? '#FF8389' : '#ffffff';
      iconDarkColorHex = '#FA4D56';
    } else {
      background = 'var(--dlThemeGray-100)';
      color = 'var(--dlThemeGray-800)';
    }
    return {background, color, iconColorHex, iconDarkColorHex};
  }, [hasErrors, isSelected, willBeRemoved]);
  return (
    <div
      style={{
        borderRadius: 8,
        background,
        padding: '2px 4px 2px 8px',
        display: 'inline-flex',
        gap: 4,
        alignItems: 'center',
        cursor: 'pointer',
      }}
      onClick={() => {
        onSelect();
      }}
      data-name={dataName}
    >
      <Typography style={{color, userSelect: 'none', pointerEvents: 'none', fontSize: 12, wordBreak: 'break-word'}} variant='valueBold'>
        {label}
      </Typography>
      <DlMuiDeleteButton
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onRemove(event);
        }}
        iconTitle={'common.confirmDialog.delete'}
        color={iconColorHex}
        darkColor={iconDarkColorHex}
      />
    </div>
  );
};

type PhRecDialogProps = {
  phRecState: PhRecState;
  signature: string;
  phRecParams: PhRecParams;
  onCancel: () => void;
  onPublish: () => void;
  formatters: Formatters;
};
const HospitalizationPhRecommendationV2PreviewDialog: React.FC<PhRecDialogProps> = (props) => {
  const {phRecState, phRecParams, onCancel, onPublish, formatters, signature} = props;
  const {t} = formatters;
  const richTextToText = useRichTextToText();
  return (
    <Modal style={{borderRadius: 16}} className='dl-big-modal' show>
      <Modal.Header closeButton={false} style={{border: 'none', padding: '8px 14px'}}>
        <Modal.Title>
          <DlMuiDynamicIcon
            iconName='phRecPublishIcon'
            useFixedViewBox='0 0 24 22'
            style={{padding: 0, paddingRight: 8}}
            svgIconStyle={{
              width: 18,
              height: 18,
            }}
          />
          <Typography
            variant='labelBold'
            style={{
              fontSize: 18,
              lineHeight: '24px',
              color: 'var(--dlThemeGray-800)',
            }}
          >
            {t('hospitalization.detail.phRec.preview.title')}
          </Typography>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{padding: '0 14px', display: 'flex', flexDirection: 'column', gap: 8}}>
        <div className='ph-rec-box'>
          <LightPreviewBox
            maxWidth='100%'
            label={t('hospitalization.detail.phRec.riskiness')}
            params={phRecParams.riskiness as SelectParams}
            chips={phRecState.riskiness ? [phRecState.riskiness] : []}
          />
        </div>
        <div className='ph-rec-box'>
          <LightPreviewBox
            maxWidth='100%'
            label={t('hospitalization.detail.phRec.riskFactors')}
            params={phRecParams.riskFactors_local as SelectParams}
            chips={phRecState.riskFactors_local}
            noDataText={t('hospitalization.detail.phRec.riskFactors.none')}
          />
        </div>
        <div className='ph-rec-box'>
          <LightPreviewBox
            maxWidth='100%'
            label={t('hospitalization.detail.phRec.summary')}
            text={richTextToText(phRecState.summaryFormatted) ?? ''}
            noDataText={t('hospitalization.detail.phRec.summary.none')}
          />
        </div>
        <div className='ph-rec-box'>
          <LightPreviewBox
            maxWidth='100%'
            label={t('hospitalization.detail.phRec.conclusion')}
            text={`${richTextToText(phRecState.conclusionFormatted) ?? ''}\n${signature}`}
          />
        </div>
        <div className='ph-rec-box'>
          <LightPreviewBox
            maxWidth='100%'
            label={t('hospitalization.detail.phRec.procedures')}
            params={phRecParams.procedures as SelectParams}
            chips={phRecState.procedures}
            noDataText={t('hospitalization.detail.phRec.procedures.none')}
          />
        </div>
      </Modal.Body>
      <Modal.Footer style={{border: 'none', justifyContent: 'space-between'}}>
        <DlMuiButton
          style={{height: 30}}
          data-name='modal-cancel-button'
          type='chip-gray'
          title={t('common.confirmDialog.back')}
          onClick={onCancel}
        />
        <DlMuiButton
          style={{height: 30}}
          data-name='modal-confirm-button'
          type='chip2-filled'
          title={t('common.confirmDialog.publish')}
          onClick={onPublish}
        />
      </Modal.Footer>
    </Modal>
  );
};
